"use client";

import type React from "react";

import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Mic,
  MessageSquare,
  MousePointer,
  Sparkles,
  ArrowRight,
  StepForwardIcon,
  Play,
  Pause,
} from "lucide-react";

type InteractionStep = {
  id: string;
  type: "code" | "visual" | "AI";
  command: string;
  before: React.ReactNode;
  after: React.ReactNode;
  icon: React.ElementType | "text" | "click";
  // command: string
  // before: React.ReactNode
  // after: React.ReactNode
  // icon: React.ElementType
};
const interactionSteps: InteractionStep[] = [
  {
    id: "step1",
    type: "code",
    command: "Create a hero section with a gradient background",
    before: (
      <div className="p-4 bg-white text-black rounded shadow">
        <h1 className="text-2xl font-bold mb-2">Welcome to my website</h1>
        <p>This is a simple landing page.</p>
      </div>
    ),
    after: (
      <div className="relative p-8 rounded-3xl shadow-2xl bg-gradient-to-br from-[#1e1b4b] via-[#4c1d95] to-[#581c87] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-fuchsia-500 via-purple-600 to-indigo-700 blur-2xl z-0" />
        <div className="relative z-10 flex flex-col items-center text-center space-y-6">
          <h1 className="text-5xl font-extrabold tracking-tight drop-shadow-md">
            Welcome to a Smarter Web
          </h1>
          <p className="text-lg max-w-lg text-white/80">
            Transform your ideas into real, beautiful interfaces instantly with
            AI.
          </p>
          <button className="bg-white text-purple-700 px-6 py-3 rounded-full font-bold shadow-md hover:scale-105 transition">
            Let's Go →
          </button>
        </div>
      </div>
    ),
    icon: Mic,
  },
  {
    id: "step2",
    type: "visual",
    command: "Make the heading larger and change to purple",
    before: (
      <div className="relative p-8 rounded-3xl shadow-2xl bg-gradient-to-br from-[#1e1b4b] via-[#4c1d95] to-[#581c87] text-white text-center">
        <h1 className="text-5xl font-extrabold">Welcome to a Smarter Web</h1>
        <p className="text-lg text-white/80 max-w-md mx-auto mt-2">
          Transform your ideas into real, beautiful interfaces instantly with
          AI.
        </p>
        <button className="bg-white text-purple-700 px-6 py-3 mt-6 rounded-full font-bold shadow-md">
          Let's Go →
        </button>
      </div>
    ),
    after: (
      <div className="relative p-10 rounded-3xl shadow-2xl bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] text-white text-center overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[conic-gradient(at_top,_var(--tw-gradient-stops))] from-fuchsia-600 via-purple-600 to-indigo-600 blur-xl z-0" />
        <div className="relative z-10 flex flex-col items-center space-y-6">
          <h1 className="text-7xl font-black tracking-tighter text-purple-300 drop-shadow-2xl">
            Welcome to a Smarter Web
          </h1>
          <p className="text-xl max-w-xl text-white/90 leading-relaxed">
            Let AI turn your concepts into design masterpieces, effortlessly.
          </p>
          <button className="bg-purple-700 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:brightness-110 transition">
            Start Building →
          </button>
        </div>
      </div>
    ),
    icon: MessageSquare,
  },
  {
    id: "step3",
    type: "AI",
    command: "Add a sign-up form below the text",
    before: (
      <div className="relative p-10 rounded-3xl shadow-2xl bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] text-white text-center">
        <h1 className="text-7xl font-black text-purple-300">
          Welcome to a Smarter Web
        </h1>
        <p className="text-xl text-white/90 mt-4">
          Let AI turn your concepts into design masterpieces, effortlessly.
        </p>
        <button className="bg-purple-700 text-white px-8 py-3 mt-6 rounded-full font-bold shadow-lg">
          Start Building →
        </button>
      </div>
    ),
    after: (
      <div className="relative p-12 rounded-3xl shadow-2xl bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] text-white text-center">
        <div className="absolute inset-0 z-0 opacity-40 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-purple-500 via-fuchsia-600 to-pink-500 blur-2xl" />
        <div className="relative z-10 flex flex-col items-center space-y-6">
          <h1 className="text-7xl font-black text-purple-300 drop-shadow-xl">
            Welcome to a Smarter Web
          </h1>
          <p className="text-xl max-w-xl text-white/90">
            Let AI turn your concepts into design masterpieces, effortlessly.
          </p>
          <form className="w-full max-w-md mt-8 bg-white/10 backdrop-blur-lg border border-white/20 p-6 rounded-xl shadow-inner space-y-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full px-4 py-3 text-black rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button className="w-full bg-purple-700 text-white py-3 rounded-md font-semibold hover:bg-purple-800 transition">
              Sign Up
            </button>
          </form>
          <button className="px-6 py-3 mt-4 bg-white text-purple-700 rounded-full font-bold shadow-md hover:scale-105 transition">
            Start Exploring →
          </button>
        </div>
      </div>
    ),
    icon: MousePointer,
  },
];

export function AIInteractionTimeline() {
  const [activeStep, setActiveStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const [isPlaying, setIsPlaying] = useState(true);

  const totalSteps = interactionSteps.length;

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % totalSteps);
    }, 3500); // Change step every 3.5 seconds

    return () => clearInterval(interval);
  }, [isPlaying, totalSteps]);

  return (
    <section
      className="py-24 px-4 relative overflow-hidden"
      id="ai-interaction"
      ref={containerRef}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-card/40 to-transparent z-0"></div>
      <motion.div
        style={{ opacity }}
        className="container mx-auto relative z-10"
      >
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            AI Interaction Showcase
          </h2>
          <p className="text-xl max-w-3xl mx-auto">
            See how Raffaello's AI understands and implements your commands
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8">
          <div className="relative">
            <div className="absolute top-0 bottom-0 left-6 w-0.5 bg-gradient-to-b from-purple-600 via-pink-500 to-purple-600"></div>

            {interactionSteps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
                className={`relative mb-12 pl-16 ${activeStep === index ? "opacity-100" : "opacity-60"}`}
                onClick={() => setActiveStep(index)}
              >
                <div
                  className={`absolute left-0 h-12 w-12 rounded-full flex items-center justify-center z-10 transition-colors ${activeStep === index
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg shadow-purple-500/20"
                      : "bg-card/80 border border-card-foreground/70"
                    }`}
                >
                  <StepForwardIcon className="h-5 w-5" />
                </div>

                <div className="mb-2">
                  <span
                    className={`text-sm px-3 py-1 rounded-full ${step.type === "code"
                        ? "bg-purple-900/30 text-purple-500 border border-purple-500/30"
                        : step.type === "visual"
                          ? "bg-blue-900/30 text-blue-500 border border-blue-500/30"
                          : "bg-amber-900/30 text-amber-600 border border-amber-500/30"
                      }`}
                  >
                    {step.type === "code"
                      ? "AI Generation"
                      : step.type === "AI"
                        ? "AI Edit"
                        : "Visual Edit"}
                  </span>
                </div>

                <div
                  className={`p-4 rounded-lg transition-colors ${activeStep === index
                      ? "bg-card/80 border border-card-foreground/70"
                      : "bg-card/90 border border-card-foreground/80"
                    }`}
                >
                  <p className="font-medium">{step.command}</p>
                </div>

                {activeStep === index && (
                  <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 hidden lg:block">
                    <ArrowRight className="h-6 w-6 text-purple-500" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 shadow-2xl">
            <div className="bg-gray-800 px-4 py-3 flex items-center justify-between border-b border-gray-700">
              <div className="flex space-x-2">
                <div className="h-3 w-3 rounded-full bg-red-500"></div>
                <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                <div className="h-3 w-3 rounded-full bg-green-500"></div>
              </div>
              <div className="text-sm font-medium text-card-foreground/30">
                Before / After
              </div>
              <div className="flex items-center space-x-1">
                <Sparkles className="h-4 w-4 text-purple-400" />
                <span className="text-xs text-purple-400">AI Powered</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-700">
              <div
                className="p-4 h-[400px] overflow-auto"
                style={{ zoom: "0.8" }}
              >
                <div className="text-sm text-gray-400 mb-4">Before</div>
                <motion.div
                  key={`before-${activeStep}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {interactionSteps[activeStep].before}
                </motion.div>
              </div>

              <div className="p-4 h-[400px] overflow-auto">
                <div className="text-sm text-gray-400 mb-4">After</div>
                <motion.div
                  key={`after-${activeStep}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div style={{ zoom: "0.8" }}>
                    {interactionSteps[activeStep].after}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
